import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Phone, Camera, Music, Thermometer, Lightbulb, CreditCard, 
  Wifi, WifiOff, RefreshCw, Settings, ChevronRight, Activity,
  CheckCircle, AlertCircle, Clock, Zap, Sliders, Star, LogOut,
  MessageSquare
} from 'lucide-react';
import confetti from 'canvas-confetti';
import ConfigTab, { FoundingBadge } from '../components/ConfigTab';
import { useWebSocket } from '../hooks/useWebSocket';
import { WsIndicator } from '../components/ConnectionBanner';
import AuditTrailTab from '../components/AuditTrailTab';
import EchoTab from '../components/EchoTab';
import MoatTab from '../components/MoatTab';
import CommunicationHub from '../components/admin/CommunicationHub';
import { useAuth } from '../contexts/AuthContext';
import { API as API_BASE } from '../lib/api';

const API_URL = API_BASE.replace(/\/api$/, '');

// ── Vitals Pulse Health Score ─────────────────────────────────────────
const HealthScoreDial = ({ token }) => {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (!token) return;
    const load = () =>
      fetch(`${API_BASE}/admin/health-score`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(setHealth)
        .catch(() => {});
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [token]);

  if (!health) return null;

  const r = 36; const circ = 2 * Math.PI * r;
  const dash = (health.score / 100) * circ;
  const color = health.color || '#00FF41';

  return (
    <div
      data-testid="command-center-health-dial"
      className="flex items-center gap-5 px-6 py-4 rounded-2xl"
      style={{ background: color + '0f', border: `1px solid ${color}28` }}
    >
      {/* Pulsing dial */}
      <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: 88, height: 88 }}>
        <svg width="88" height="88" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
          {/* Vitals pulse rings */}
          <motion.circle cx="44" cy="44" r={r + 6} fill="none" stroke={color} strokeWidth="1.5"
            animate={{ opacity: [0.05, 0.5, 0.05] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
          <motion.circle cx="44" cy="44" r={r + 11} fill="none" stroke={color} strokeWidth="0.8"
            animate={{ opacity: [0, 0.2, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }} />
          {/* Track */}
          <circle cx="44" cy="44" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="6" />
          {/* Score arc */}
          <circle cx="44" cy="44" r={r} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1.2s ease' }} />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-xl font-black leading-none" style={{ color }}>{health.score}</span>
          <span className="text-[8px] text-gray-600 uppercase tracking-wider mt-0.5">/ 100</span>
        </div>
      </div>
      {/* Text */}
      <div>
        <p className="font-bold text-base" style={{ color }}>{health.label}</p>
        <p className="text-xs text-gray-500 mt-0.5">System Health — Last 24h</p>
        <div className="flex gap-3 mt-2">
          {health.critical_events_24h > 0 && (
            <span className="text-[10px] text-red-400">{health.critical_events_24h} critical</span>
          )}
          {health.high_events_24h > 0 && (
            <span className="text-[10px] text-amber-400">{health.high_events_24h} high</span>
          )}
          <span className="text-[10px] text-gray-600">{health.total_events_24h} events</span>
        </div>
      </div>
    </div>
  );
};

// System type configurations
const SYSTEM_TYPES = {
  pos: {
    id: 'pos',
    name: 'Point of Sale',
    icon: CreditCard,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    providers: ['Toast', 'Square', 'Clover', 'TouchBistro'],
    features: ['Menu Sync', 'Orders', 'Tables', 'Inventory'],
    description: 'Sync menus, orders, and table status'
  },
  phone: {
    id: 'phone',
    name: 'AI Receptionist',
    icon: Phone,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    providers: ['Vapi + Twilio'],
    features: ['Reservations', 'Orders', 'FAQ', 'Routing'],
    description: 'Echo handles calls 24/7'
  },
  camera: {
    id: 'camera',
    name: 'Vision AI',
    icon: Camera,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    providers: ['RTSP', 'IP Webcam', 'USB'],
    features: ['Kitchen Monitor', 'Plating QC', 'Safety', 'Inventory'],
    description: 'Apollo watches the kitchen'
  },
  music: {
    id: 'music',
    name: 'Ambiance',
    icon: Music,
    color: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/30',
    providers: ['Sonos', 'Spotify', 'Apple Music'],
    features: ['Auto Playlists', 'Volume Control', 'Mood Sync'],
    description: 'AI-controlled atmosphere'
  },
  hvac: {
    id: 'hvac',
    name: 'Climate',
    icon: Thermometer,
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    providers: ['Nest', 'Ecobee', 'Honeywell'],
    features: ['Auto Temp', 'Energy Savings', 'Schedules'],
    description: 'Smart temperature control'
  },
  lighting: {
    id: 'lighting',
    name: 'Lighting',
    icon: Lightbulb,
    color: 'from-yellow-500 to-orange-500',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    providers: ['Philips Hue', 'LIFX', 'Lutron'],
    features: ['Dimming', 'Color', 'Schedules', 'Alerts'],
    description: 'Automated ambiance lighting'
  }
};

// Connection status badge
const StatusBadge = ({ status }) => {
  const configs = {
    connected: { icon: CheckCircle, text: 'Connected', color: 'text-emerald-400 bg-emerald-400/10' },
    disconnected: { icon: WifiOff, text: 'Disconnected', color: 'text-gray-400 bg-gray-400/10' },
    error: { icon: AlertCircle, text: 'Error', color: 'text-red-400 bg-red-400/10' },
    syncing: { icon: RefreshCw, text: 'Syncing...', color: 'text-blue-400 bg-blue-400/10' },
    pending: { icon: Clock, text: 'Setup Required', color: 'text-amber-400 bg-amber-400/10' }
  };
  
  const config = configs[status] || configs.disconnected;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className={`w-3.5 h-3.5 ${status === 'syncing' ? 'animate-spin' : ''}`} />
      {config.text}
    </span>
  );
};

// System Card Component
const SystemCard = ({ system, connection, onConnect, onSync, onConfigure }) => {
  const config = SYSTEM_TYPES[system];
  const Icon = config.icon;
  const status = connection?.status || 'disconnected';
  const lastSync = connection?.last_sync;
  
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${config.borderColor} ${config.bgColor} p-6 transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20`}>
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${config.color} opacity-5`} />
      
      {/* Header */}
      <div className="relative flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${config.color} shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{config.name}</h3>
            <p className="text-xs text-gray-400">{config.description}</p>
          </div>
        </div>
        <StatusBadge status={status} />
      </div>
      
      {/* Provider info */}
      {connection?.provider && (
        <div className="relative mb-4 p-3 rounded-lg bg-black/20 border border-white/5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">{connection.provider}</span>
            {lastSync && (
              <span className="text-xs text-gray-500">
                Last sync: {new Date(lastSync).toLocaleTimeString()}
              </span>
            )}
          </div>
          {connection.sync_count && (
            <div className="mt-2 flex gap-4 text-xs text-gray-400">
              <span>📋 {connection.sync_count.menu || 0} items</span>
              <span>🪑 {connection.sync_count.tables || 0} tables</span>
              <span>📦 {connection.sync_count.orders || 0} orders</span>
            </div>
          )}
        </div>
      )}
      
      {/* Features */}
      <div className="relative mb-4">
        <div className="flex flex-wrap gap-2">
          {config.features.map((feature, idx) => (
            <span 
              key={idx}
              className="px-2 py-1 rounded-md bg-white/5 text-xs text-gray-400 border border-white/10"
            >
              {feature}
            </span>
          ))}
        </div>
      </div>
      
      {/* Actions */}
      <div className="relative flex gap-2">
        {status === 'connected' ? (
          <>
            <button
              onClick={() => onSync(system)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm text-white transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Sync Now
            </button>
            <button
              onClick={() => onConfigure(system)}
              className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-gray-400 transition-colors"
            >
              <Settings className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => onConnect(system)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r ${config.color} text-white text-sm font-medium transition-all hover:opacity-90`}
          >
            <Zap className="w-4 h-4" />
            Connect
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Connect Modal
const ConnectModal = ({ system, onClose, onSubmit }) => {
  const [provider, setProvider] = useState('');
  const [credentials, setCredentials] = useState({});
  const [loading, setLoading] = useState(false);
  const [setupGuide, setSetupGuide] = useState(null);
  
  const config = SYSTEM_TYPES[system];
  
  useEffect(() => {
    // Fetch setup guide for POS
    if (system === 'pos' && provider) {
      fetch(`${API_URL}/api/pos/setup-guide/${provider.toLowerCase()}`)
        .then(res => res.json())
        .then(data => setSetupGuide(data))
        .catch(() => setSetupGuide(null));
    }
  }, [system, provider]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(system, provider, credentials);
    setLoading(false);
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-[#0A1244] rounded-2xl border border-white/10 shadow-2xl">
        {/* Header */}
        <div className={`p-6 border-b border-white/10 bg-gradient-to-r ${config.color} rounded-t-2xl`}>
          <div className="flex items-center gap-3">
            <config.icon className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-xl font-bold text-white">Connect {config.name}</h2>
              <p className="text-sm text-white/70">{config.description}</p>
            </div>
          </div>
        </div>
        
        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Provider Selection */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Select Provider</label>
            <div className="grid grid-cols-2 gap-2">
              {config.providers.map((p, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setProvider(p.split(' ')[0].toLowerCase())}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    provider === p.split(' ')[0].toLowerCase()
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white'
                      : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          
          {/* Setup Guide */}
          {setupGuide && (
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <h4 className="font-medium text-blue-400 mb-2">Setup Steps:</h4>
              <ol className="text-xs text-gray-400 space-y-1">
                {setupGuide.steps?.slice(0, 4).map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
              {setupGuide.documentation_url && (
                <a 
                  href={setupGuide.documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-xs text-blue-400 hover:underline"
                >
                  Full documentation →
                </a>
              )}
            </div>
          )}
          
          {/* Credential Fields (dynamic based on provider) */}
          {provider && system === 'pos' && (
            <div className="space-y-3">
              {provider === 'toast' && (
                <>
                  <input
                    type="text"
                    placeholder="Client ID"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, client_id: e.target.value})}
                  />
                  <input
                    type="password"
                    placeholder="Client Secret"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, client_secret: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Restaurant GUID"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, restaurant_guid: e.target.value})}
                  />
                </>
              )}
              {provider === 'square' && (
                <>
                  <input
                    type="password"
                    placeholder="Access Token"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, access_token: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Location ID"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, location_id: e.target.value})}
                  />
                  <select
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, environment: e.target.value})}
                  >
                    <option value="">Select Environment</option>
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </>
              )}
              {provider === 'clover' && (
                <>
                  <input
                    type="password"
                    placeholder="API Key"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, api_key: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Merchant ID"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, merchant_id: e.target.value})}
                  />
                  <select
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, environment: e.target.value})}
                  >
                    <option value="">Select Environment</option>
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </>
              )}
              {provider === 'touchbistro' && (
                <>
                  <input
                    type="password"
                    placeholder="Partner API Key"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, api_key: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Venue ID"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:outline-none"
                    onChange={(e) => setCredentials({...credentials, venue_id: e.target.value})}
                  />
                </>
              )}
            </div>
          )}
          
          {/* Coming Soon for non-POS */}
          {provider && system !== 'pos' && (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
              <p className="text-amber-400">
                {config.name} integration coming soon!
              </p>
              <p className="text-xs text-gray-400 mt-1">
                We're working with {provider} to enable this feature.
              </p>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!provider || loading || system !== 'pos'}
              className={`flex-1 px-4 py-3 rounded-lg bg-gradient-to-r ${config.color} text-white font-medium transition-all ${
                !provider || loading || system !== 'pos' ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
              }`}
            >
              {loading ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Command Center Component
const CommandCenter = () => {
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectModal, setConnectModal] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [token, setToken] = useState(null);
  const [activeTab, setActiveTab] = useState('integrations');
  const [isFoundingMember, setIsFoundingMember] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState('default');
  const [restaurants, setRestaurants] = useState([
    { id: 'default', name: 'Argus Xenia Corporate' },
    { id: 'sandbox_taverna', name: 'Opa! Taverna (Sandbox)' },
  ]);
  const [restaurantStatus, setRestaurantStatus] = useState('available');
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Confetti burst for first-time Google login
  useEffect(() => {
    if (location.state?.isNew) {
      const duration = 3000;
      const end = Date.now() + duration;
      const colors = ['#D4AF37', '#FFD700', '#FFA500', '#00FF41', '#ffffff'];
      (function frame() {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
      // Clear the state so confetti doesn't re-trigger
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // Real-time WS connection — shows live status in header + feeds Audit Trail
  const { status: wsStatus, lastEvent } = useWebSocket('default');

  // Load connections on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('argus_admin_token');
    if (storedToken) {
      setToken(storedToken);
      fetchConnections(storedToken);
      fetchFoundingStatus(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchFoundingStatus = async (authToken) => {
    try {
      const res = await fetch(`${API_URL}/api/config/default`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsFoundingMember(data.founding_member_mode === true);
      }
    } catch {}
  };
  
  const fetchConnections = async (authToken) => {
    try {
      const res = await fetch(`${API_URL}/api/pos/connections`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (!res.ok) {
        setLoading(false);
        return;
      }

      const data = await res.json();
      
      // Convert array to object by provider
      const connMap = {};
      data.connections?.forEach(conn => {
        connMap[conn.provider] = conn;
      });
      
      // Add mock data for other systems (since they're not implemented yet)
      connMap['phone'] = { status: 'connected', provider: 'Vapi + Twilio', last_sync: new Date().toISOString() };
      
      setConnections(connMap);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleConnect = async (system, provider, credentials) => {
    if (system !== 'pos') {
      alert(`${SYSTEM_TYPES[system].name} integration coming soon!`);
      setConnectModal(null);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/pos/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider, credentials })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setConnections(prev => ({
          ...prev,
          [provider]: { status: data.status, provider: provider.charAt(0).toUpperCase() + provider.slice(1) }
        }));
        setConnectModal(null);
        alert(data.message);
      } else {
        alert(data.detail || 'Connection failed');
      }
    } catch (err) {
      alert('Connection error: ' + err.message);
    }
  };
  
  const handleSync = async (system) => {
    if (system !== 'pos') return;
    
    setSyncing(system);
    try {
      const provider = Object.keys(connections).find(k => 
        ['toast', 'square', 'clover', 'touchbistro'].includes(k)
      );
      
      if (!provider) {
        alert('No POS connected');
        return;
      }
      
      const res = await fetch(`${API_URL}/api/pos/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider, sync_type: 'full' })
      });
      
      const data = await res.json();
      alert(`Synced: ${JSON.stringify(data.result?.syncs || data, null, 2)}`);
    } catch (err) {
      alert('Sync error: ' + err.message);
    } finally {
      setSyncing(null);
    }
  };
  
  const handleConfigure = (system) => {
    alert(`Configure ${system} - coming soon!`);
  };
  
  // Calculate stats
  const connectedCount = Object.values(connections).filter(c => c?.status === 'connected').length;
  const totalSystems = Object.keys(SYSTEM_TYPES).length;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#040F49] flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading Command Center...</span>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const TABS = [
    { id: 'integrations', label: 'Integrations',  icon: Zap      },
    { id: 'comms',        label: 'Comms Hub',      icon: MessageSquare },
    { id: 'echo',         label: 'Echo',           icon: Phone    },
    { id: 'moat',         label: 'Data Moat',      icon: Activity },
    { id: 'config',       label: 'AI Persona',    icon: Sliders  },
    { id: 'audit',        label: 'Audit Trail',   icon: Activity },
  ];

  return (
    <div
      className="min-h-screen p-6 transition-all duration-700"
      style={{
        background: "#040F49",
        ...(isFoundingMember && {
          boxShadow: "inset 0 0 0 2px rgba(212,175,55,0.25), inset 0 0 60px rgba(212,175,55,0.04)",
        }),
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4 mb-2">
              <div
                className="p-3 rounded-xl shadow-lg transition-all duration-500"
                style={{
                  background: isFoundingMember
                    ? "linear-gradient(135deg, #D4AF37, #b8941f)"
                    : "linear-gradient(135deg, #D4AF37, #f59e0b)",
                  boxShadow: isFoundingMember
                    ? "0 4px 20px rgba(212,175,55,0.5)"
                    : "0 4px 16px rgba(245,158,11,0.2)",
                }}
              >
                <Activity className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Command Center</h1>
                <p className="text-gray-400">Your restaurant's nervous system</p>
              </div>
            </div>
            {/* Founding Badge */}
            {isFoundingMember && <FoundingBadge />}
          </div>

          {/* Founding glow bar */}
          {isFoundingMember && (
            <div
              className="h-px w-full mt-3 mb-2"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.6), transparent)",
              }}
            />
          )}

          {/* Status Bar */}
          <div className="mt-4 flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 flex-wrap">
            {/* Multi-Tenant Selector */}
            <select
              data-testid="restaurant-selector"
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
            >
              {restaurants.map(r => (
                <option key={r.id} value={r.id} className="bg-[#0A1244]">{r.name}</option>
              ))}
            </select>
            <div className="h-4 w-px bg-white/20" />

            {/* Restaurant Status Toggle */}
            <div className="flex items-center gap-2">
              {['available', 'busy', 'closed'].map(s => (
                <button
                  key={s}
                  data-testid={`status-${s}`}
                  onClick={async () => {
                    setRestaurantStatus(s);
                    await fetch(`${API_BASE}/team/status`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ restaurant_id: selectedRestaurant, status: s }),
                    });
                  }}
                  className="px-3 py-1 rounded-full text-xs font-medium transition-all"
                  style={{
                    background: restaurantStatus === s
                      ? s === 'available' ? 'rgba(16,185,129,0.2)' : s === 'busy' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'
                      : 'rgba(255,255,255,0.05)',
                    color: restaurantStatus === s
                      ? s === 'available' ? '#10b981' : s === 'busy' ? '#f59e0b' : '#ef4444'
                      : '#6b7280',
                    border: `1px solid ${restaurantStatus === s
                      ? s === 'available' ? 'rgba(16,185,129,0.3)' : s === 'busy' ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)'
                      : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div className="h-4 w-px bg-white/20" />

            <div className="flex items-center gap-2">
              <Wifi className="w-4 h-4 text-emerald-400" />
              <span className="text-white text-sm font-medium">{connectedCount}/{totalSystems} Online</span>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <WsIndicator status={wsStatus} />
              <button
                onClick={() => fetchConnections(token)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh All
              </button>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm transition-colors border border-red-500/20"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mt-5 flex gap-1 p-1 rounded-xl bg-white/5 border border-white/8 w-fit">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`tab-${id}`}
                onClick={() => setActiveTab(id)}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: activeTab === id ? "rgba(212,175,55,0.12)" : "transparent",
                  color: activeTab === id ? "#D4AF37" : "#9ca3af",
                  border: activeTab === id ? "1px solid rgba(212,175,55,0.3)" : "1px solid transparent",
                }}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'integrations' && (
          <>
            {/* Vitals Pulse — primary widget on the landing tab */}
            <div className="mb-6">
              <HealthScoreDial token={token} />
            </div>

            {/* Systems Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.keys(SYSTEM_TYPES).map(systemKey => (
                <SystemCard
                  key={systemKey}
                  system={systemKey}
                  connection={connections[systemKey] || connections[systemKey.toLowerCase()]}
                  onConnect={() => setConnectModal(systemKey)}
                  onSync={() => handleSync(systemKey)}
                  onConfigure={() => handleConfigure(systemKey)}
                />
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-[#D4AF37]/10 to-amber-600/10 border border-[#D4AF37]/30">
              <h3 className="text-lg font-semibold text-[#D4AF37] mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button className="p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left transition-colors">
                  <Phone className="w-5 h-5 text-blue-400 mb-2" />
                  <span className="block text-white text-sm font-medium">Test Call Echo</span>
                  <span className="text-xs text-gray-500">+1 (778) 907-9914</span>
                </button>
                <button className="p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left transition-colors">
                  <Camera className="w-5 h-5 text-purple-400 mb-2" />
                  <span className="block text-white text-sm font-medium">Kitchen View</span>
                  <span className="text-xs text-gray-500">Apollo Vision</span>
                </button>
                <button className="p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left transition-colors">
                  <CreditCard className="w-5 h-5 text-emerald-400 mb-2" />
                  <span className="block text-white text-sm font-medium">Sync POS</span>
                  <span className="text-xs text-gray-500">Pull latest data</span>
                </button>
                <button
                  onClick={() => setActiveTab('config')}
                  className="p-4 rounded-xl hover:bg-white/10 border text-left transition-colors"
                  style={{ background: "rgba(212,175,55,0.06)", borderColor: "rgba(212,175,55,0.2)" }}
                >
                  <Sliders className="w-5 h-5 text-[#D4AF37] mb-2" />
                  <span className="block text-white text-sm font-medium">AI Persona</span>
                  <span className="text-xs text-gray-500">Configure Echo</span>
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'config' && (
          <ConfigTab token={token} onFoundingChange={setIsFoundingMember} />
        )}

        {activeTab === 'comms' && (
          <CommunicationHub token={token} />
        )}

        {activeTab === 'echo' && (
          <EchoTab token={token} />
        )}

        {activeTab === 'moat' && (
          <MoatTab token={token} />
        )}

        {activeTab === 'audit' && (
          <AuditTrailTab token={token} wsLastEvent={lastEvent} />
        )}
      </div>

      {/* Connect Modal */}
      {connectModal && (
        <ConnectModal
          system={connectModal}
          onClose={() => setConnectModal(null)}
          onSubmit={handleConnect}
        />
      )}
    </div>
  );
};

export default CommandCenter;
